'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, BookOpen, ClipboardList, MessageSquare, Video } from 'lucide-react';
import { AppNotification } from '@/lib/services/notificationService';
import { createClient } from '@/lib/supabase/client';

interface ToastItem extends AppNotification {
  visible: boolean;
}

interface Props {
  recipientId: string;
}

const TYPE_CONFIG: Record<AppNotification['type'], { icon: React.ReactNode; color: string; bg: string; border: string }> = {
  registration: {
    icon: <BookOpen data-component-id="src/components/NotificationToast.tsx:17:10" data-component-path="src/components/NotificationToast.tsx" data-component-line="17" data-component-end-line="17" data-component-file="NotificationToast.tsx" data-component-name="BookOpen" data-component-content="%7B%22elementName%22%3A%22BookOpen%22%7D" size={16} />,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  assignment: {
    icon: <ClipboardList data-component-id="src/components/NotificationToast.tsx:23:10" data-component-path="src/components/NotificationToast.tsx" data-component-line="23" data-component-end-line="23" data-component-file="NotificationToast.tsx" data-component-name="ClipboardList" data-component-content="%7B%22elementName%22%3A%22ClipboardList%22%7D" size={16} />,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  message: {
    icon: <MessageSquare data-component-id="src/components/NotificationToast.tsx:29:10" data-component-path="src/components/NotificationToast.tsx" data-component-line="29" data-component-end-line="29" data-component-file="NotificationToast.tsx" data-component-name="MessageSquare" data-component-content="%7B%22elementName%22%3A%22MessageSquare%22%7D" size={16} />,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
  },
  live_session: {
    icon: <Video data-component-id="src/components/NotificationToast.tsx:35:10" data-component-path="src/components/NotificationToast.tsx" data-component-line="35" data-component-end-line="35" data-component-file="NotificationToast.tsx" data-component-name="Video" data-component-content="%7B%22elementName%22%3A%22Video%22%7D" size={16} />,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
  },
};

// Single shared supabase client for this component module
const supabase = createClient();

function dbToNotification(row: Record<string, unknown>): AppNotification {
  return {
    id: row.id as string,
    recipientId: row.recipient_id as string,
    recipientRole: row.recipient_role as AppNotification['recipientRole'],
    type: row.type as AppNotification['type'],
    title: row.title as string,
    body: row.body as string,
    courseId: row.course_id as string | undefined,
    referenceId: row.reference_id as string | undefined,
    isRead: row.is_read as boolean,
    createdAt: row.created_at as string,
  };
}

export default function NotificationToast({ recipientId }: Props) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, visible: false } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  const handlerRef = useRef<(n: AppNotification) => void>(() => {});

  useEffect(() => {
    handlerRef.current = (n: AppNotification) => {
      const item: ToastItem = { ...n, visible: true };
      setToasts(prev => [item, ...prev].slice(0, 5));
      const timer = setTimeout(() => dismissToast(n.id), 5000);
      timersRef.current.set(n.id, timer);
    };
  }, [dismissToast]);

  useEffect(() => {
    if (!recipientId) return;

    // Remove existing channel using the same client instance
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Create a unique channel name to avoid conflicts across re-renders
    const channelName = `toast-notifications:${recipientId}:${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${recipientId}`,
        },
        (payload) => {
          if (payload.new) handlerRef.current(dbToNotification(payload.new as Record<string, unknown>));
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId]);

  if (toasts.length === 0) return null;

  return (
    <div data-component-id="src/components/NotificationToast.tsx:126:4" data-component-path="src/components/NotificationToast.tsx" data-component-line="126" data-component-end-line="156" data-component-file="NotificationToast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-4%20right-4%20z-%5B9999%5D%20flex%20flex-col%20gap-2%20pointer-events-none%22%7D" className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none" aria-live="polite">
      {toasts.map((toast) => {
        const cfg = TYPE_CONFIG[toast.type];
        return (
          <div data-component-id="src/components/NotificationToast.tsx:130:10" data-component-path="src/components/NotificationToast.tsx" data-component-line="130" data-component-end-line="153" data-component-file="NotificationToast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            key={toast.id}
            className={`pointer-events-auto flex items-start gap-3 w-80 max-w-[calc(100vw-2rem)] p-3.5 rounded-xl border shadow-lg bg-white transition-all duration-300 ${
              toast.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            } ${cfg.border}`}
            role="alert"
          >
            <div data-component-id="src/components/NotificationToast.tsx:137:12" data-component-path="src/components/NotificationToast.tsx" data-component-line="137" data-component-end-line="139" data-component-file="NotificationToast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg} ${cfg.color}`}>
              {cfg.icon}
            </div>
            <div data-component-id="src/components/NotificationToast.tsx:140:12" data-component-path="src/components/NotificationToast.tsx" data-component-line="140" data-component-end-line="152" data-component-file="NotificationToast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
              <div data-component-id="src/components/NotificationToast.tsx:141:14" data-component-path="src/components/NotificationToast.tsx" data-component-line="141" data-component-end-line="150" data-component-file="NotificationToast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20gap-2%22%7D" className="flex items-start justify-between gap-2">
                <p data-component-id="src/components/NotificationToast.tsx:142:16" data-component-path="src/components/NotificationToast.tsx" data-component-line="142" data-component-end-line="142" data-component-file="NotificationToast.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20font-semibold%20text-gray-900%20leading-snug%22%7D" className="text-xs font-semibold text-gray-900 leading-snug">{toast.title}</p>
                <button data-component-id="src/components/NotificationToast.tsx:143:16" data-component-path="src/components/NotificationToast.tsx" data-component-line="143" data-component-end-line="149" data-component-file="NotificationToast.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex-shrink-0%20p-0.5%20rounded%20hover%3Abg-gray-100%20text-gray-400%20hover%3Atext-gray-600%20transition-colors%22%7D"
                  onClick={() => dismissToast(toast.id)}
                  className="flex-shrink-0 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Dismiss notification"
                >
                  <X data-component-id="src/components/NotificationToast.tsx:148:18" data-component-path="src/components/NotificationToast.tsx" data-component-line="148" data-component-end-line="148" data-component-file="NotificationToast.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%7D" size={12} />
                </button>
              </div>
              <p data-component-id="src/components/NotificationToast.tsx:151:14" data-component-path="src/components/NotificationToast.tsx" data-component-line="151" data-component-end-line="151" data-component-file="NotificationToast.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B11px%5D%20text-gray-500%20mt-0.5%20leading-snug%20line-clamp-2%22%7D" className="text-[11px] text-gray-500 mt-0.5 leading-snug line-clamp-2">{toast.body}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
